import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import {
  BookingPassportForm,
  PASSPORT_TYPE_LABEL,
  addPassportBooking,
  sanitizeFileName,
} from "src/store/reducers/bookings";
import { useAppDispatch } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";

type IAddPassportBookingCanvas = {
  Title: string;
  show: boolean;
  bookingId: string;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddPassportBookingCanvas = ({
  Title,
  show,
  bookingId,
  setShow,
}: IAddPassportBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    errors,
    setErrors,
  } = useFormik<{
    passport_number: string;
    fullname: string;
    nationality: string;
    date_of_birth: string;
    issue_date: string;
    expiry_date: string;
    issue_country: string;
    place_of_issue: string;
    passport_type: number;
    notes: string;
  }>({
    initialValues: {
      passport_number: "",
      fullname: "",
      nationality: "",
      date_of_birth: "",
      issue_date: "",
      expiry_date: "",
      issue_country: "",
      place_of_issue: "",
      passport_type: 1,
      notes: "",
    },
    validationSchema: Yup.object({
      passport_number: KeyRequired("Passport number"),
      fullname: KeyRequired("Full name"),
      passport_type: KeyRequired("Passport type"),
      nationality: KeyRequired("Nationality"),
      date_of_birth: Yup.string().required("Date of birth is required"),
      issue_date: Yup.string().required("Issue date is required"),
      expiry_date: Yup.string().required("Expiry date is required"),
      issue_country: KeyRequired("Issue country"),
      place_of_issue: KeyRequired("Place of issue"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      addPassport({ ...values, booking: bookingId });
    },
  });

  const addPassport = useCallback(
    async (values: BookingPassportForm) => {
      try {
        await dispatch(addPassportBooking({ form: values, files })).unwrap();
        toast.success("Passport is added successfully");
        setShow(false);
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, files, setErrors, setShow]
  );

  useEffect(() => {
    resetForm();
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetForm, show]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button
          type="button"
          className="btn-close"
          onClick={() => setShow(false)}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fullname"
                  labelText="Full Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.fullname}
                  touched={touched.fullname}
                  errors={errors.fullname}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="passport_number"
                  labelText="Passport Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.passport_number}
                  touched={touched.passport_number}
                  errors={errors.passport_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="nationality"
                  labelText="Nationality"
                  required={true}
                  handleChange={handleChange}
                  value={values.nationality}
                  touched={touched.nationality}
                  errors={errors.nationality}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="date_of_birth"
                  labelText="Date of Birth"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.date_of_birth}
                  touched={touched.date_of_birth}
                  errors={errors.date_of_birth}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="issue_date"
                  labelText="Issue Date"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.issue_date}
                  touched={touched.issue_date}
                  errors={errors.issue_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="expiry_date"
                  labelText="Expiry Date"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.expiry_date}
                  touched={touched.expiry_date}
                  errors={errors.expiry_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="issue_country"
                  labelText="Issue Country"
                  required={true}
                  handleChange={handleChange}
                  value={values.issue_country}
                  touched={touched.issue_country}
                  errors={errors.issue_country}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="place_of_issue"
                  labelText="Place of Issue"
                  required={true}
                  handleChange={handleChange}
                  value={values.place_of_issue}
                  touched={touched.place_of_issue}
                  errors={errors.place_of_issue}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="passport_type"
                  labelText="Passport Type"
                  required={true}
                  handleChange={handleChange}
                  value={values.passport_type}
                  touched={touched.passport_type}
                  errors={errors.passport_type}
                >
                  {Object.entries(PASSPORT_TYPE_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="notes"
                  labelText="Notes"
                  handleChange={handleChange}
                  value={values.notes}
                  touched={touched.notes}
                  errors={errors.notes}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Attachments</Form.Label>
                <Form.Control
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Accepted formats: JPG, JPEG, PNG, PDF
                </Form.Text>
                {files.length > 0 && (
                  <div className="mt-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">
                          {sanitizeFileName(file.name)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeFile(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link
              to={"#"}
              onClick={() => setShow(false)}
              className="btn btn-danger light ms-1"
            >
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default AddPassportBookingCanvas;
